import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
	MomentDateAdapter,
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
} from '@angular/material-moment-adapter';
import { ActivatedRoute } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { PT_BR_DATE_FORMATS } from '../../../../utils/date-formats.utils';
import { NumberUtils } from '../../../../utils/number/number.utils';
import {
	ACCOMODATION,
	BenefitRequest,
	COVERAGE,
	DISMISS_RETIRED,
} from '../../models/benefits.model';
import { PartnerRegistration } from '../../models/organizations.model';
import {
	editBenefitRegStart,
	fetchBenefitRegStart,
	fetchUFCitiesClear,
	fetchPartnersStart,
	createBenefitStart,
	restoreFormReset,
} from '../../state/organizations.actions';
import {
	selectBenefit,
	selectPartners,
	selectRestoreForm,
} from '../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-benefit-registration',
	styleUrls: ['./benefit-registration.component.scss'],
	templateUrl: './benefit-registration.component.html',
})
export class BenefitRegistrationComponent implements OnInit, OnDestroy {
	accommodation = ACCOMODATION;
	ansOptions = [
		{ label: 'Sim', value: true },
		{ label: 'Não', value: false },
	];
	benefitId: string = '';
	benefitsTabs = [
		{ label: 'Plano de saúde', path: 'saude' },
		{ label: 'Plano dental', path: 'dental' },
		{ label: 'Plano conjugado', path: 'conjugado' }, // Added Plano Conjugado
	];
	bilForm: UntypedFormGroup;
	copForm: UntypedFormGroup;
	coverage = COVERAGE;
	days = Array.from({ length: 30 }, (v, idx) => idx + 1);
	dis_ret = DISMISS_RETIRED;

	inputId: string = String(Math.random());
	loading: boolean = true;
	orgId: string = '';
	partners: PartnerRegistration[] = [];
	partnersByTab: PartnerRegistration[] = [];
	payForm: UntypedFormGroup;
	regForm: UntypedFormGroup;
	rmbForm: UntypedFormGroup;

	private readonly unsubscribe$: Subject<void> = new Subject();

	constructor(
		private readonly fb: UntypedFormBuilder,
		private readonly activatedRoute: ActivatedRoute,
		private readonly store: Store,
	) {
		this.regForm = this.fb.group({
			accommodation: [null, [Validators.required]],
			code_ans: [null, [Validators.required]],
			coverage: [null, [Validators.required]],
			lack_ans: [null, [Validators.required]],
			name: [null, [Validators.required]],
			partner_id: [null, [Validators.required]],
			type: ['HEALTH', [Validators.required]],
		});
		this.bilForm = this.fb.group({
			cutting_day: [null, [Validators.required]],
			dismiss_retired: [null, [Validators.required]],
			pay_day: [null, [Validators.required]],
			pro_rata: [null, [Validators.required]],
			start_date: [null, [Validators.required]],
		});
		this.copForm = this.fb.group({
			complex_exams_type: ['R$', [Validators.required]],
			complex_exams_value: [null],
			emergency_room_type: ['R$', [Validators.required]],
			emergency_room_value: [null],
			hospitalization_type: ['R$', [Validators.required]],
			hospitalization_value: [null],
			queries_type: ['R$', [Validators.required]],
			queries_value: [null],
			simple_exams_type: ['R$', [Validators.required]],
			simple_exams_value: [null],
			therapies_type: ['R$', [Validators.required]],
			therapies_value: [null],
		});
		this.rmbForm = this.fb.group({
			coin: [null],
			multiple: [null],
			queries: [null],
		});
		this.payForm = this.fb.group({
			average_value: [true, [Validators.required]],
			average_value_benefits: [null, [Validators.required]],
			fifty_four_to_fifty_eight: [null],
			forty_four_to_forty_eight: [null],
			forty_nine_to_fifty_three: [null],
			from_fifty_nine: [null],
			nineteen_to_twenty_three: [null],
			thirty_four_to_thirty_eight: [null],
			thirty_nine_to_forty_three: [null],
			twenty_four_to_twenty_eight: [null],
			twenty_nine_to_thirty_three: [null],
			value_by_age: [false, [Validators.required]],
			zero_to_eighteen: [null],
		});
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.store.dispatch(fetchUFCitiesClear());
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.activatedRoute.params.subscribe(({ id, bId }) => {
			this.orgId = id;
			if (!bId) {
				this.loading = false;
			} else {
				this.benefitId = bId;
				this.store
					.select(selectBenefit)
					.pipe(takeUntil(this.unsubscribe$))
					.subscribe((b) => {
						if (b) {
							const isHealthOrConjugated =
								b.type === 'HEALTH' || b.type === 'CONJUGATED';
							const optionals = isHealthOrConjugated
								? [Validators.required]
								: [];
							this.regForm = this.fb.group({
								accommodation: [b.accommodation, optionals],
								code_ans: [b.code_ans, [Validators.required]],
								coverage: [b.coverage, optionals],
								lack_ans: [b.lack_ans, [Validators.required]],
								name: [b.name, [Validators.required]],
								partner_id: [
									{ disabled: true, value: b.partner_id },
									[Validators.required],
								],
								type: [b.type, [Validators.required]],
							});
							this.bilForm = this.fb.group({
								cutting_day: [
									b.rules_billing.cutting_day,
									[Validators.required],
								],
								dismiss_retired: [
									b.rules_billing.dismiss_retired,
									[Validators.required],
								],
								pay_day: [b.rules_billing.pay_day, [Validators.required]],
								pro_rata: [b.rules_billing.pro_rata, [Validators.required]],
								start_date: [
									moment(b.rules_billing.start_date),
									[Validators.required],
								],
							});
							this.rmbForm = this.fb.group({
								coin: [String(b.coin).replace('.', ',')],
								multiple: [String(b.multiple).replace('.', ',')],
								queries: [String(b.queries).replace('.', ',')],
							});
							this.copForm = this.fb.group({
								complex_exams_type: [
									b.co_participation.complex_exams_type,
									[Validators.required],
								],
								complex_exams_value: [
									String(b.co_participation.complex_exams_value).replace(
										'.',
										',',
									),
								],
								emergency_room_type: [
									b.co_participation.emergency_room_type,
									[Validators.required],
								],
								emergency_room_value: [
									String(b.co_participation.emergency_room_value).replace(
										'.',
										',',
									),
								],
								hospitalization_type: [
									b.co_participation.hospitalization_type,
									[Validators.required],
								],
								hospitalization_value: [
									String(b.co_participation.hospitalization_value).replace(
										'.',
										',',
									),
								],
								queries_type: [
									b.co_participation.queries_type,
									[Validators.required],
								],
								queries_value: [
									String(b.co_participation.queries_value)
										.replace('.', ',')
										.replace('.', ','),
								],
								simple_exams_type: [
									b.co_participation.simple_exams_type,
									[Validators.required],
								],
								simple_exams_value: [
									String(b.co_participation.simple_exams_value).replace(
										'.',
										',',
									),
								],
								therapies_type: [
									b.co_participation.therapies_type,
									[Validators.required],
								],
								therapies_value: [
									String(b.co_participation.therapies_value).replace('.', ','),
								],
							});
							this.payForm = this.fb.group({
								average_value: [
									b.payments.average_value,
									[Validators.required],
								],
								average_value_benefits: [
									String(b.payments.average_value_benefits).replace('.', ','),
								],
								fifty_four_to_fifty_eight: [
									String(b.payments.fifty_four_to_fifty_eight).replace(
										'.',
										',',
									),
								],
								forty_four_to_forty_eight: [
									String(b.payments.forty_four_to_forty_eight).replace(
										'.',
										',',
									),
								],
								forty_nine_to_fifty_three: [
									String(b.payments.forty_nine_to_fifty_three).replace(
										'.',
										',',
									),
								],
								from_fifty_nine: [
									String(b.payments.from_fifty_nine).replace('.', ','),
								],
								nineteen_to_twenty_three: [
									String(b.payments.nineteen_to_twenty_three).replace('.', ','),
								],
								thirty_four_to_thirty_eight: [
									String(b.payments.thirty_four_to_thirty_eight).replace(
										'.',
										',',
									),
								],
								thirty_nine_to_forty_three: [
									String(b.payments.thirty_nine_to_forty_three).replace(
										'.',
										',',
									),
								],
								twenty_four_to_twenty_eight: [
									String(b.payments.twenty_four_to_twenty_eight).replace(
										'.',
										',',
									),
								],
								twenty_nine_to_thirty_three: [
									String(b.payments.twenty_nine_to_thirty_three).replace(
										'.',
										',',
									),
								],
								value_by_age: [b.payments.value_by_age, [Validators.required]],
								zero_to_eighteen: [
									String(b.payments.zero_to_eighteen).replace('.', ','),
								],
							});
							// Set partnersByTab for editing
							if (b.type === 'HEALTH' || b.type === 'CONJUGATED') {
								this.partnersByTab = this.partners.filter((p) => p.health_plan);
							} else if (b.type === 'DENTAL') {
								this.partnersByTab = this.partners.filter((p) => p.dental_plan);
							}
							this.loading = false;
							this.onPaymentTypeChange(b.payments.average_value);
						}
					});
				this.store.dispatch(fetchBenefitRegStart({ id, benefitId: bId }));
			}
		});
		this.store
			.select(selectPartners)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe((partners) => {
				if (partners) {
					this.partners = partners;
					this.partnersByTab = this.partners.filter((p) => p.health_plan);
				}
			});
		this.store.dispatch(fetchPartnersStart());
	}

	onPaymentTypeChange(average: boolean): void {
		this.payForm.get('average_value')!.setValue(average);
		this.payForm.get('value_by_age')!.setValue(!average);
		this.payForm
			.get('average_value_benefits')!
			.setValidators(average ? [Validators.required] : []);
		this.payForm
			.get('zero_to_eighteen')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('nineteen_to_twenty_three')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('twenty_four_to_twenty_eight')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('twenty_nine_to_thirty_three')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('thirty_four_to_thirty_eight')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('thirty_nine_to_forty_three')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('forty_four_to_forty_eight')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('forty_nine_to_fifty_three')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('fifty_four_to_fifty_eight')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm
			.get('from_fifty_nine')!
			.setValidators(average ? [] : [Validators.required]);
		this.payForm.get('average_value_benefits')!.updateValueAndValidity();
		this.payForm.get('zero_to_eighteen')!.updateValueAndValidity();
		this.payForm.get('nineteen_to_twenty_three')!.updateValueAndValidity();
		this.payForm.get('twenty_four_to_twenty_eight')!.updateValueAndValidity();
		this.payForm.get('twenty_nine_to_thirty_three')!.updateValueAndValidity();
		this.payForm.get('thirty_four_to_thirty_eight')!.updateValueAndValidity();
		this.payForm.get('thirty_nine_to_forty_three')!.updateValueAndValidity();
		this.payForm.get('forty_four_to_forty_eight')!.updateValueAndValidity();
		this.payForm.get('forty_nine_to_fifty_three')!.updateValueAndValidity();
		this.payForm.get('fifty_four_to_fifty_eight')!.updateValueAndValidity();
		this.payForm.get('from_fifty_nine')!.updateValueAndValidity();
	}

	onPlanTypeChange(type: string): void {
		const isHealth = type === 'saude';
		const isDental = type === 'dental';
		const isConjugated = type === 'conjugado';

		const requiresHealthFields = isHealth || isConjugated;
		const optionals = requiresHealthFields ? [Validators.required] : [];

		this.regForm.get('coverage')!.setValidators(optionals);
		this.regForm.get('accommodation')!.setValidators(optionals);

		if (isHealth) {
			this.regForm.get('type')!.setValue('HEALTH');
			this.partnersByTab = this.partners.filter((p) => p.health_plan);
		} else if (isDental) {
			this.regForm.get('type')!.setValue('DENTAL');
			this.partnersByTab = this.partners.filter((p) => p.dental_plan);
			this.regForm.get('coverage')!.setValue(null);
			this.regForm.get('accommodation')!.setValue(null);
		} else if (isConjugated) {
			this.regForm.get('type')!.setValue('CONJUGATED');
			this.partnersByTab = this.partners.filter((p) => p.health_plan);
		}

		this.regForm.get('partner_id')!.setValue(null);
		this.regForm.get('partner_id')!.markAsUntouched();

		this.regForm.updateValueAndValidity();
	}

	onSubmit(): void {
		const request = this.processForm();

		if (request) {
			this.store
				.select(selectRestoreForm)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((restore) => {
					if (restore) {
						this.updateValidation();
						this.loading = false;
					}
				});
			if (this.benefitId) {
				this.store.dispatch(
					editBenefitRegStart({
						id: this.orgId,
						benefitId: this.benefitId,
						request,
					}),
				);
			} else {
				this.store.dispatch(createBenefitStart({ id: this.orgId, request }));
			}
			this.loading = true;
		}
	}

	processForm(): BenefitRequest | null {
		if (
			this.regForm.invalid ||
			this.bilForm.invalid ||
			this.copForm.invalid ||
			this.rmbForm.invalid ||
			this.payForm.invalid
		) {
			return null;
		}

		const reg = this.regForm.value;
		const bil = this.bilForm.value;
		const cop = this.copForm.value;
		const rmb = this.rmbForm.value;
		const pay = this.payForm.value;
		bil.start_date = moment(bil.start_date).format('YYYY-MM-DD hh:mm:ssZZ');
		cop.queries_value = cop.queries_value
			? Number(String(cop.queries_value).replace(',', '.'))
			: null;
		cop.simple_exams_value = cop.simple_exams_value
			? Number(String(cop.simple_exams_value).replace(',', '.'))
			: null;
		cop.complex_exams_value = cop.complex_exams_value
			? Number(String(cop.complex_exams_value).replace(',', '.'))
			: null;
		cop.emergency_room_value = cop.emergency_room_value
			? Number(String(cop.emergency_room_value).replace(',', '.'))
			: null;
		cop.hospitalization_value = cop.hospitalization_value
			? Number(String(cop.hospitalization_value).replace(',', '.'))
			: null;
		cop.therapies_value = cop.therapies_value
			? Number(String(cop.therapies_value).replace(',', '.'))
			: null;
		rmb.multiple = rmb.multiple
			? Number(String(rmb.multiple).replace(',', '.'))
			: null;
		rmb.queries = rmb.queries
			? Number(String(rmb.queries).replace(',', '.'))
			: null;
		if (pay.value_by_age) {
			pay.zero_to_eighteen = NumberUtils.convertCommaToDot(
				pay.zero_to_eighteen,
			);
			pay.nineteen_to_twenty_three = NumberUtils.convertCommaToDot(
				pay.nineteen_to_twenty_three,
			);
			pay.twenty_four_to_twenty_eight = NumberUtils.convertCommaToDot(
				pay.twenty_four_to_twenty_eight,
			);
			pay.twenty_nine_to_thirty_three = NumberUtils.convertCommaToDot(
				pay.twenty_nine_to_thirty_three,
			);
			pay.thirty_four_to_thirty_eight = NumberUtils.convertCommaToDot(
				pay.thirty_four_to_thirty_eight,
			);
			pay.thirty_nine_to_forty_three = NumberUtils.convertCommaToDot(
				pay.thirty_nine_to_forty_three,
			);
			pay.forty_four_to_forty_eight = NumberUtils.convertCommaToDot(
				pay.forty_four_to_forty_eight,
			);
			pay.forty_nine_to_fifty_three = NumberUtils.convertCommaToDot(
				pay.forty_nine_to_fifty_three,
			);
			pay.fifty_four_to_fifty_eight = NumberUtils.convertCommaToDot(
				pay.fifty_four_to_fifty_eight,
			);
			pay.from_fifty_nine = NumberUtils.convertCommaToDot(pay.from_fifty_nine);
			pay.average_value_benefits = null;
		} else {
			pay.average_value_benefits = Number(
				String(pay.average_value_benefits).replace(',', '.'),
			);
			pay.zero_to_eighteen = null;
			pay.nineteen_to_twenty_three = null;
			pay.twenty_four_to_twenty_eight = null;
			pay.twenty_nine_to_thirty_three = null;
			pay.thirty_four_to_thirty_eight = null;
			pay.thirty_nine_to_forty_three = null;
			pay.forty_four_to_forty_eight = null;
			pay.forty_nine_to_fifty_three = null;
			pay.fifty_four_to_fifty_eight = null;
			pay.from_fifty_nine = null;
		}

		const request: BenefitRequest = {
			...reg,
			...rmb,
			co_participation: cop,
			payments: pay,
			rules_billing: bil,
			type: this.regForm.get('type')!.value,
		};

		return request;
	}

	shouldDisableSaveBtn(): boolean {
		return (
			this.regForm.invalid ||
			this.bilForm.invalid ||
			this.copForm.invalid ||
			this.rmbForm.invalid ||
			this.payForm.invalid ||
			(this.regForm.untouched &&
				this.bilForm.untouched &&
				this.copForm.untouched &&
				this.rmbForm.untouched &&
				this.payForm.untouched)
		);
	}

	updateInputType(control: string, value: string): void {
		const valueControl = control.replace('type', 'value');
		this.copForm.get(control)!.setValue(value);
		this.copForm.get(valueControl)!.setValue(null);
	}

	updateValidation(): void {
		for (const control in this.regForm.controls) {
			this.regForm.get(control)!.markAsTouched();
			this.regForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.bilForm.controls) {
			this.bilForm.get(control)!.markAsTouched();
			this.bilForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.copForm.controls) {
			this.copForm.get(control)!.markAsTouched();
			this.copForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.rmbForm.controls) {
			this.rmbForm.get(control)!.markAsTouched();
			this.rmbForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}

		for (const control in this.payForm.controls) {
			this.payForm.get(control)!.markAsTouched();
			this.payForm.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
